import {
  IExperienceTemplateResource,
  ISubTaskVisitTemplate,
  StorageContext
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import React, { SetStateAction } from 'react';
import { DataFieldWithLabel } from '../../../../../../../../components/DataFieldWithLabel';
import {
  FormRow,
  FormRowGroup
} from '../../../../../../../../components/Form/FormRow';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import { TextFieldWithReplacementTokens } from '../../../../../../../../components/TextFieldWithReplacementTokens';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_VISIT
} from '../../../validation';
import { OnCompleteHooks, OnCompleteHooksReadOnly } from './onCompleteHooks';

export const BlockSettingsSubTaskVisit = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl,
  customFields,
  resources,
  onChangeResources,
  goToSubPage,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskVisitTemplate;
  onChange: (nextValue: ISubTaskVisitTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const validateUrl = useFieldValidator(setValidation, VALIDATORS_VISIT, 'url');
  const validateHooks = useFieldValidator(
    setValidation,
    VALIDATORS_VISIT,
    'hooks'
  );

  const invalidUrlReason = validation?.fields.url || null;

  return (
    <>
      <FormRowGroup>
        <FormRow>
          <TextFieldWithReplacementTokens
            label={t('Label')}
            value={value.data.label}
            fullWidth
            tokens={tokens}
            onChange={(v) => {
              const nextValue = {
                ...value,
                data: {
                  ...value.data,
                  label: v
                }
              };
              onChange(nextValue);
            }}
          />
        </FormRow>
        <FormRow>
          <TextFieldWithReplacementTokens
            autoFocus
            label={t('URL')}
            value={value.data.url}
            fullWidth
            variant="outlined"
            error={!!invalidUrlReason}
            helperText={invalidUrlReason}
            onBlur={() => validateUrl(value, expTpl)}
            onChange={(nextUrl) => {
              const nextValue = {
                ...value,
                data: {
                  ...value.data,
                  url: nextUrl
                }
              };
              invalidUrlReason && validateUrl(nextValue, expTpl);
              onChange(nextValue);
            }}
            tokens={tokens}
          />
        </FormRow>
      </FormRowGroup>
      <OnCompleteHooks
        value={value.hooks.onComplete}
        tokens={tokens}
        onChange={(nextHooks) => {
          const nextValue = {
            ...value,
            hooks: {
              ...value.hooks,
              onComplete: nextHooks
            }
          };
          validateHooks(nextValue, expTpl);
          onChange(nextValue);
        }}
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        goToSubPage={goToSubPage}
        validation={validation}
        showOnCompleteHookSettings={showOnCompleteHookSettings}
        storageContext={StorageContext.SUBTASK}
      />
    </>
  );
};

export const BlockSettingsSubTaskVisitReadOnly = ({
  value,
  resources,
  expTpl,
  customFields,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskVisitTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <>
      <FormRowGroup>
        <FormRow>
          <DataFieldWithLabel label={t('Link URL')}>
            {value.data.url || '-'}
          </DataFieldWithLabel>
        </FormRow>
        <FormRow>
          <DataFieldWithLabel label={t('Link Label')}>
            {value.data.label || '-'}
          </DataFieldWithLabel>
        </FormRow>
      </FormRowGroup>
      {showOnCompleteHookSettings && (
        <OnCompleteHooksReadOnly
          storageContext={StorageContext.SUBTASK}
          tokens={tokens}
          value={value.hooks.onComplete}
          resources={resources}
          expTpl={expTpl}
          customFields={customFields}
        />
      )}
    </>
  );
};
